import {useTranslation} from "react-i18next";
import CommonClass from "@/utils/common";
import clsx from "clsx";
import st from "@/styles/CreditLineListItem.module.scss";
import StaticRangeSlider from "@/ui/StaticRangeSlider.tsx";
import Button from "@/ui/Button.tsx";
import DashboardClass from "@/utils/dashboard_class";
import {useNavigate} from 'react-router-dom';
import LoanInterface from "@/types_and_interfaces/loan_interface.ts";
import useCommon from "@/hooks/common.ts";
import React from "react";
import PayToday from "@/ui/PayToday.tsx";
import {useAtom} from "jotai";
import {profile} from "@/atoms/profile.ts";

interface ItemProps {
    obj: LoanInterface,
    set_request_transaction?():void
}


const CreditLineDisbursedListItem:React.FC<ItemProps> = ({obj, set_request_transaction}) => {

    const {
        created_at,
        capital,
        avail_capital,
        transactions,
        inst_amount,
        loan_age,
        urn,
        billing_deadline_days,
        status,
        product_name,
        is_transaction_application_allowed
    } = obj

    const {t} = useTranslation()
    const dc = DashboardClass
    const navigate = useNavigate();
    const common_hook = useCommon()
    const [user] = useAtom(profile)

    const data_row = [
        {
            label: t("amount"),
            value: CommonClass.formatMoneyAmount(Number(capital)) + " " + t("currency"),
            visible: ["closed", "finished"]?.includes(status)
        },
        {
            label: t("sum_credit_line"),
            value:  CommonClass.formatMoneyAmount(Number(avail_capital)) + " " + t("currency"),
            visible: ["approved", "disbursed"]?.includes(status)
        },
        {
            label: t("min_monthly_payment"),
            value:  CommonClass.formatMoneyAmount(Number(inst_amount)) + " " + t("currency"),
            visible: ["approved", "disbursed"]?.includes(status)
        },
        {
            label: t("next_payment"),
            value: billing_deadline_days + " " + t(CommonClass.time_word_handling(billing_deadline_days)),
            visible: ["approved", "disbursed"]?.includes(status)
        }
    ]

    const disabledStatuses = ["pending_approval", "approved_and_need_sign"];
    const enable = transactions?.filter(x => disabledStatuses.includes(x.status)) ?? [];
    const disabled = user?.identity_card_is_expired || (enable.length > 0 || !is_transaction_application_allowed);
    const loan_amn = Number(capital) - Number(avail_capital)

    const getCreditLineDetails = () => {
        navigate(dc.dashboard_routes.CreditLine + `/${urn}`)
    }

    return (
        <div
            className={clsx(
                st.credit_line_list_item,
                loan_age > 0 && st.restant,
                ["closed", "finished"]?.includes(obj?.status) && st.paid,
                (["approved", "disbursed"]?.includes(obj?.status) && !loan_age) && st.accepted,
                ["canceled"]?.includes(obj?.status) && st.canceled
            )}
            onClick={()=> !["canceled"]?.includes(obj?.status) && getCreditLineDetails()}
        >
            <div className={st.top}>
                <div className={st.top_left}>
                    <strong
                        className={st.urn}
                        onClick={(e) => {
                            e.stopPropagation();
                            common_hook.copyToClipboard(urn)
                        }}
                    >
                        {t("credit_line_nr").replace('{{name}}', t(product_name))}  {urn}
                    </strong>

                    <span>
                        {t("requested")} {common_hook.formatMomentDate(created_at)}
                    </span>
                </div>

                <div className={st.top_right}>
                    <div className={st.status_badge}>
                        {loan_age > 0 ? t("late") : t("line_credit_status_"+ status)}
                    </div>
                </div>
            </div>

            <div className={st.center}>
                {["canceled"]?.includes(obj?.status) && (
                    <div className={st.loan_sum}>
                        <span>{t("amount")}</span>
                        <strong>
                            {CommonClass.formatMoneyAmount(Number(capital))} {t("currency")}
                        </strong>
                    </div>
                )}

                {data_row.map((data_obj, i) => {
                    const {label, value, visible} = data_obj

                    return visible && (
                        <div
                            key={`data_obj_${i}`}
                            className={st.data_obj}
                        >
                            <span>
                                {label}
                            </span>

                            {label === t("next_payment") && billing_deadline_days === 0 ? (
                                <PayToday />
                            ) : (
                                <strong>
                                    {value}
                                </strong>
                            )}
                        </div>
                    )
                })}
            </div>

            {["approved", "disbursed"]?.includes(obj?.status) && (
                <div className={st.bottom}>
                    <StaticRangeSlider
                        to_fixed
                        value={loan_amn}
                        max={Number(capital)}
                        danger={loan_age > 0}
                    />

                    <div className={st.controllers}>
                        <Button
                            onClick={(e)=>{
                                e.stopPropagation();
                                set_request_transaction && set_request_transaction()
                            }}
                            disabled={disabled}
                            className={clsx(st.btn, st.btn_outlined)}
                        >
                            {t("request_transaction")}
                        </Button>

                        {disabled && (
                            <div className={"flex flex-col gap-[6px]"}>
                                {user?.identity_card_is_expired ? (
                                    <span className={st.error}>
                                        {t('identity_card_is_expired')}
                                    </span>
                                ): (
                                    <>
                                        {!!enable.length && (
                                            <span className={st.warn}>
                                                {t("record_already_exists")}
                                            </span>
                                        )}

                                        {(!enable.length && !is_transaction_application_allowed) && (
                                            <span className={st.warn}>
                                                {t("denied_apply_cr_line")}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default CreditLineDisbursedListItem